<template>
  <div style="background:#fff; padding:20px;">
    <!-- 顶部搜索区 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="商家ID">
          <a-input-number
            v-model="query.merchantId"
            :min="1"
            placeholder="请输入商家ID"
            style="width: 140px"
          />
        </a-form-item>
        <!--
          任务状态：按 Requirement 21 修订后的 9 状态机展示
          1=待审核 / 2=待修改 / 3=已发布 / 4=待竞价 / 5=待支付 /
          6=待服务 / 7=待验收 / 8=已取消 / 9=已完成
        -->
        <a-form-item label="任务状态">
          <a-select
            v-model="query.status"
            placeholder="全部"
            allow-clear
            style="width: 140px"
          >
            <a-select-option v-for="item in filterStatusOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="审核状态">
          <a-select
            v-model="query.auditStatus"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option :value="0">待审核</a-select-option>
            <a-select-option :value="1">已通过</a-select-option>
            <a-select-option :value="2">已拒绝</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="时间区间">
          <a-range-picker
            v-model="query.timeRange"
            :show-time="{ format: 'HH:mm:ss' }"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :placeholder="['起始时间', '结束时间']"
            style="width: 380px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表 -->
    <a-radio-group :value="query.status" button-style="solid" style="margin-bottom:16px" @change="onStatusShortcut">
      <a-radio-button value="ONLINE">进行中</a-radio-button>
      <a-radio-button :value="9">已完成</a-radio-button>
      <a-radio-button value="CANCEL_REQUEST">取消待客服处理</a-radio-button>
    </a-radio-group>
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      :scroll="{ x: 1800 }"
      @change="handleTableChange"
    >
      <span slot="cancelRequest" slot-scope="text, record">
        <a-tag v-if="record.cancelRequestAt" color="orange">取消待客服处理</a-tag>
        <span v-else>-</span>
      </span>
      <!-- 任务状态着色（9 状态机） -->
      <span slot="status" slot-scope="text">
        <a-tag :color="getStatusColor(text)">{{ getStatusLabel(text) }}</a-tag>
      </span>

      <!-- 顶置状态 -->
      <span slot="topStatus" slot-scope="text, record">
        <a-tag v-if="record.isTop === 1" color="red">顶置</a-tag>
        <span v-else style="color:#bfbfbf;">-</span>
      </span>

      <!-- 审核状态着色 -->
      <span slot="auditStatus" slot-scope="text">
        <a-tag v-if="text === 0" color="orange">待审核</a-tag>
        <a-tag v-else-if="text === 1" color="green">已通过</a-tag>
        <a-tag v-else-if="text === 2" color="red">已拒绝</a-tag>
        <span v-else>-</span>
      </span>

      <!-- 修改意见列：仅在 status=2 待修改 或字段非空时展示文本，其余渲染 - -->
      <span slot="revisionRemark" slot-scope="text, record">
        <span v-if="record.status === 2 && text" :title="text" style="color:#fa541c;">
          {{ text }}
        </span>
        <span v-else style="color:#bfbfbf;">-</span>
      </span>

      <!-- 操作列 -->
      <span slot="action" slot-scope="text, record">
        <a v-permission="'app:task:list'" style="color:#1890ff" @click="handleOpenDetail(record)">详情</a>
        <a-divider type="vertical" />
        <a
          v-permission="'app:task:list'"
          :style="{ color: isClosedStatus(record.status) ? '#999' : 'red', cursor: isClosedStatus(record.status) ? 'not-allowed' : 'pointer' }"
          @click="handleOpenClose(record)"
        >{{ record.cancelRequestAt ? '同意取消' : '强制关闭' }}</a>
        <a v-if="record.cancelRequestAt" v-permission="'app:task:list'" style="margin-left:12px" @click="handleRejectCancellation(record)">拒绝取消</a>
        <a-divider type="vertical" />
        <a
          v-if="record.canForceComplete"
          v-permission="'app:task:complete'"
          style="color:#52c41a"
          @click="handleForceComplete(record)"
        >订单完成</a>
        <a-divider v-if="record.canForceComplete" type="vertical" />
        <a-button
          v-permission="'app:task:list'"
          size="small"
          :type="record.isTop === 1 ? 'default' : 'primary'"
          :loading="topSubmittingId === record.id"
          @click="handleSetTop(record)"
        >{{ record.isTop === 1 ? '取消顶置' : '顶置' }}</a-button>
      </span>
    </a-table>

    <!-- 任务详情弹窗 -->
    <a-modal
      v-model="detailVisible"
      title="任务详情"
      width="960px"
      :footer="null"
      :destroy-on-close="true"
      @cancel="handleCloseDetail"
    >
      <a-spin :spinning="detailLoading">
        <!-- 基础信息卡 -->
        <a-descriptions
          v-if="detail.task"
          bordered
          size="small"
          :column="2"
          style="margin-bottom: 16px;"
        >
          <a-descriptions-item label="ID">{{ detail.task.id }}</a-descriptions-item>
          <!-- 任务编号 task_no（Requirement 21 新增展示字段） -->
          <a-descriptions-item label="任务编号">{{ detail.task.taskNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="商家ID">{{ detail.task.merchantId }}</a-descriptions-item>
          <!-- 下单模式 order_mode：1=一口价 / 2=竞价 -->
          <a-descriptions-item label="下单模式">{{ getOrderModeLabel(detail.task.orderMode) }}</a-descriptions-item>
          <a-descriptions-item label="标题" :span="2">{{ detail.task.title }}</a-descriptions-item>
          <a-descriptions-item label="服务类目">{{ detail.task.categoryName || '-' }}</a-descriptions-item>
          <a-descriptions-item label="名额上限">{{ detail.task.quotaLimit == null ? '-' : detail.task.quotaLimit }}</a-descriptions-item>
          <a-descriptions-item label="服务地址" :span="2">{{ detail.task.serviceAddress }}</a-descriptions-item>
          <a-descriptions-item label="经度">{{ detail.task.longitude == null ? '-' : detail.task.longitude }}</a-descriptions-item>
          <a-descriptions-item label="纬度">{{ detail.task.latitude == null ? '-' : detail.task.latitude }}</a-descriptions-item>
          <a-descriptions-item label="验收标准" :span="2">{{ detail.task.acceptanceCriteria }}</a-descriptions-item>
          <a-descriptions-item label="任务状态">
            <a-tag :color="getStatusColor(detail.task.status)">{{ getStatusLabel(detail.task.status) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="顶置状态">
            <a-tag v-if="detail.task.isTop === 1" color="red">顶置</a-tag>
            <span v-else>未顶置</span>
          </a-descriptions-item>
          <a-descriptions-item label="审核状态">
            <a-tag v-if="detail.task.auditStatus === 0" color="orange">待审核</a-tag>
            <a-tag v-else-if="detail.task.auditStatus === 1" color="green">已通过</a-tag>
            <a-tag v-else-if="detail.task.auditStatus === 2" color="red">已拒绝</a-tag>
            <span v-else>-</span>
          </a-descriptions-item>
          <!-- 修改意见 revision_remark：仅 status=2 待修改时高亮展示 -->
          <a-descriptions-item label="修改意见" :span="2">
            <span v-if="detail.task.status === 2 && detail.task.revisionRemark" style="color:#fa541c;">
              {{ detail.task.revisionRemark }}
            </span>
            <span v-else>{{ detail.task.revisionRemark || '-' }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="报价数">{{ detail.task.biddingCount }}</a-descriptions-item>
          <a-descriptions-item label="成交金额">{{ detail.task.totalAmount }}</a-descriptions-item>
          <a-descriptions-item label="选中师傅ID">{{ detail.task.selectedMasterId == null ? '-' : detail.task.selectedMasterId }}</a-descriptions-item>
          <a-descriptions-item label="审核拒绝原因" :span="2">{{ detail.task.auditRejectReason || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核操作人ID">{{ detail.task.auditOperatorId == null ? '-' : detail.task.auditOperatorId }}</a-descriptions-item>
          <a-descriptions-item label="审核时间">{{ detail.task.auditAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付单号">{{ detail.task.payOutTradeNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付时间">{{ detail.task.payTime || '-' }}</a-descriptions-item>
                      <a-descriptions-item label="取消申请时间">{{ detail.task.cancelRequestAt || '-' }}</a-descriptions-item>
            <a-descriptions-item label="取消申请原因">{{ detail.task.cancelRequestReason || '-' }}</a-descriptions-item>
            <a-descriptions-item label="提交验收时间">{{ detail.task.finishApplyTime || '-' }}</a-descriptions-item>
            <a-descriptions-item label="完成时间">{{ detail.task.finishTime || '-' }}</a-descriptions-item>
          <a-descriptions-item label="关闭时间">{{ detail.task.cancelTime || detail.task.closedAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="关闭原因" :span="2">{{ detail.task.cancelReason || detail.task.closeReason || '-' }}</a-descriptions-item>
          <a-descriptions-item label="关闭操作人ID">{{ detail.task.cancelOperatorId || detail.task.closedByOperatorId || '-' }}</a-descriptions-item>
          <a-descriptions-item label="是否人工结算">{{ detail.task.manualSettleRequired === 1 ? '是' : '否' }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ detail.task.createdAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="更新时间">{{ detail.task.updatedAt || '-' }}</a-descriptions-item>
        </a-descriptions>

        <!--
          商品分组明细（Task_Item_Group）
          数据来自详情接口返回的 itemGroups 字段
        -->
        <div style="margin-top: 8px; font-weight: 600; font-size: 14px;">
          商品分组明细（共 {{ detail.itemGroups.length }} 组）
        </div>
        <a-table
          :columns="itemGroupColumns"
          :data-source="detail.itemGroups"
          rowKey="id"
          size="small"
          :pagination="false"
          style="margin-top: 8px;"
        >
          <!-- 服务项目名称：service_item_names 数组拼接展示 -->
          <span slot="serviceItemNames" slot-scope="text, record">
            {{ formatServiceItemNames(record) }}
          </span>
          <!-- 图片缩略图：images 数组逐张渲染，限制 max-height -->
          <span slot="images" slot-scope="text, record">
            <template v-if="Array.isArray(record.images) && record.images.length > 0">
              <img
                v-for="(url, idx) in record.images"
                :key="idx"
                :src="url"
                style="width:48px;height:48px;object-fit:cover;margin-right:6px;border-radius:4px;cursor:pointer;"
                @click="handlePreviewImage(url)"
              />
            </template>
            <span v-else style="color:#bfbfbf;">-</span>
          </span>
        </a-table>

        <div style="margin-top: 16px; font-weight: 600; font-size: 14px;">
          资质要求
        </div>
        <div style="margin-top: 8px;">
          <template v-if="detail.requiredCerts.length">
            <a-tag v-for="cert in detail.requiredCerts" :key="cert.id || cert.certTypeId">
              {{ cert.certTypeNameSnapshot || cert.name || cert.certTypeId }}
            </a-tag>
          </template>
          <span v-else style="color:#bfbfbf;">无</span>
        </div>

        <!-- 报价列表 -->
        <div style="margin-top: 16px; font-weight: 600; font-size: 14px;">
          报价列表（共 {{ detail.biddings.length }} 条）
        </div>
        <a-table
          :columns="biddingColumns"
          :data-source="detail.biddings"
          rowKey="id"
          size="small"
          :pagination="false"
          style="margin-top: 8px;"
        >
          <span slot="isSelected" slot-scope="text">
            <a-tag v-if="text === 1" color="green">已选中</a-tag>
            <a-tag v-else color="default">未选中</a-tag>
          </span>
        </a-table>

        <!--
          状态变更流水（app_task_status_log）
          数据来自详情接口返回的 statusLog 字段
        -->
        <div style="margin-top: 16px; font-weight: 600; font-size: 14px;">
          状态变更流水（共 {{ detail.statusLog.length }} 条）
        </div>
        <a-table
          :columns="statusLogColumns"
          :data-source="detail.statusLog"
          rowKey="id"
          size="small"
          :pagination="false"
          style="margin-top: 8px;"
        >
          <span slot="fromStatus" slot-scope="text">
            <a-tag v-if="text != null" :color="getStatusColor(text)">{{ getStatusLabel(text) }}</a-tag>
            <span v-else style="color:#bfbfbf;">（创建）</span>
          </span>
          <span slot="toStatus" slot-scope="text">
            <a-tag :color="getStatusColor(text)">{{ getStatusLabel(text) }}</a-tag>
          </span>
        </a-table>
      </a-spin>
    </a-modal>

    <!-- 强制关闭弹窗 -->
    <a-modal
      v-model="closeVisible"
      title="强制关闭任务"
      :confirm-loading="closeSubmitting"
      :destroy-on-close="true"
      @ok="handleSubmitClose"
      @cancel="handleCloseCloseModal"
    >
      <a-form-model
        ref="closeForm"
        :model="closeForm"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="任务ID">
          <span>{{ closeForm.id }}</span>
        </a-form-model-item>
        <a-form-model-item label="关闭原因" required>
          <a-textarea
            v-model="closeForm.reason"
            :rows="4"
            :max-length="200"
            placeholder="请输入关闭原因 (1-200 字)"
          />
          <div style="text-align:right; color:#999; font-size:12px;">
            {{ (closeForm.reason || '').length }} / 200
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

  </div>
</template>

<script>
import moment from 'moment'
import {
  getAppTaskList,
  getAppTaskDetail,
  forceCloseAppTask,
  setAppTaskTop,
  rejectAppTaskCancellation,
  forceCompleteAppTask
} from '@/api'

// 9 状态机静态字典：1/2/3/4/5/6/7/8/9
// 与 Requirement 21 修订条款保持一致
const STATUS_OPTIONS = [
  { value: 1, label: '待审核', color: 'orange' },
  { value: 2, label: '待修改', color: 'volcano' },
  { value: 3, label: '已发布', color: 'blue' },
  { value: 4, label: '待竞价', color: 'geekblue' },
  { value: 5, label: '待支付', color: 'gold' },
  { value: 6, label: '待服务', color: 'cyan' },
  { value: 7, label: '待验收', color: 'purple' },
  { value: 8, label: '已取消', color: 'default' },
  { value: 9, label: '已完成', color: 'green' }
]

const ONLINE_STATUS_LIST = [3, 4, 5, 6, 7]

// 下单模式字典：1=一口价 / 2=竞价
const ORDER_MODE_LABEL = {
  1: '一口价',
  2: '竞价'
}

// 操作人类型 operator_type 中文映射
const OPERATOR_TYPE_LABEL = {
  MERCHANT: '商家',
  MASTER: '师傅',
  OPERATOR: '运营',
  SYSTEM: '系统'
}

export default {
  name: 'AppTaskList',
  data() {
    return {
      data: [],
      loading: false,
      // 状态选项暴露给模板使用
      statusOptions: STATUS_OPTIONS,
      filterStatusOptions: [
        { value: 'ONLINE', label: '线上中' },
        { value: 'CANCEL_REQUEST', label: '取消待客服处理' }
      ].concat(STATUS_OPTIONS),
      // 搜索条件
      query: {
        merchantId: undefined,
        status: 'ONLINE',
        auditStatus: undefined,
        // a-range-picker v-model: [startStr, endStr] (因 valueFormat 已声明为字符串)
        timeRange: []
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      // 详情弹窗
      detailVisible: false,
      detailLoading: false,
      detail: {
        task: null,
        biddings: [],
        // 商品分组明细（Task_Item_Group）：后端字段约定 itemGroups
        itemGroups: [],
        requiredCerts: [],
        // 状态变更流水（app_task_status_log）：后端字段约定 statusLog
        statusLog: []
      },
      // 强制关闭弹窗
      closeVisible: false,
      closeSubmitting: false,
      topSubmittingId: null,
      closeForm: {
        id: null,
        reason: ''
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
        {
          title: '顶置',
          key: 'topStatus',
          width: 80,
          scopedSlots: { customRender: 'topStatus' }
        },
        { title: '客服处理', key: 'cancelRequest', width: 150, scopedSlots: { customRender: 'cancelRequest' } },
        { title: '商家ID', dataIndex: 'merchantId', width: 100 },
        { title: '标题', dataIndex: 'title', ellipsis: true, width: 200 },
        { title: '服务类目', dataIndex: 'categoryName', width: 120 },
        { title: '服务地址', dataIndex: 'serviceAddress', ellipsis: true, width: 200 },
        { title: '名额', dataIndex: 'quotaLimit', width: 70 },
        { title: '报价数', dataIndex: 'biddingCount', width: 80 },
        { title: '成交金额', dataIndex: 'totalAmount', width: 110 },
        {
          title: '任务状态',
          dataIndex: 'status',
          width: 110,
          scopedSlots: { customRender: 'status' }
        },
        {
          title: '审核状态',
          dataIndex: 'auditStatus',
          width: 100,
          scopedSlots: { customRender: 'auditStatus' }
        },
        {
          title: '修改意见',
          dataIndex: 'revisionRemark',
          width: 200,
          ellipsis: true,
          scopedSlots: { customRender: 'revisionRemark' }
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 170 },
        {
          title: '操作',
          key: 'action',
          width: 410,
          fixed: 'right',
          scopedSlots: { customRender: 'action' }
        }
      ],
      biddingColumns: [
        { title: '报价ID', dataIndex: 'id', width: 80 },
        { title: '师傅ID', dataIndex: 'masterId', width: 100 },
        { title: '报价金额', dataIndex: 'biddingAmount', width: 120 },
        { title: '报价说明', dataIndex: 'biddingDesc', ellipsis: true },
        {
          title: '是否选中',
          dataIndex: 'isSelected',
          width: 100,
          scopedSlots: { customRender: 'isSelected' }
        },
        { title: '报价时间', dataIndex: 'createdAt', width: 170 }
      ],
      // 商品分组明细列定义
      itemGroupColumns: [
        { title: '商品名称', dataIndex: 'productName', width: 160, ellipsis: true },
        { title: '数量', dataIndex: 'quantity', width: 80 },
        { title: '单价', dataIndex: 'unitPrice', width: 100 },
        {
          title: '服务项目',
          key: 'serviceItemNames',
          ellipsis: true,
          scopedSlots: { customRender: 'serviceItemNames' }
        },
        { title: '备注', dataIndex: 'remark', ellipsis: true },
        {
          title: '图片',
          key: 'images',
          width: 220,
          scopedSlots: { customRender: 'images' }
        }
      ],
      // 状态变更流水列定义
      statusLogColumns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        {
          title: '原状态',
          dataIndex: 'fromStatus',
          width: 100,
          scopedSlots: { customRender: 'fromStatus' }
        },
        {
          title: '新状态',
          dataIndex: 'toStatus',
          width: 100,
          scopedSlots: { customRender: 'toStatus' }
        },
        {
          title: '操作人类型',
          dataIndex: 'operatorType',
          width: 110,
          customRender: (text) => OPERATOR_TYPE_LABEL[text] || text || '-'
        },
        {
          title: '操作人ID',
          dataIndex: 'operatorId',
          width: 100,
          customRender: (text) => (text == null ? '-' : text)
        },
        { title: '变更时间', dataIndex: 'changeAt', width: 170 },
        { title: '变更原因', dataIndex: 'changeReason', ellipsis: true }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    onStatusShortcut(event) {
      this.query.status = event.target.value
      this.pagination.current = 1
      this.loadData()
    },
    // 取状态中文标签
    getStatusLabel(status) {
      const item = STATUS_OPTIONS.find((o) => o.value === status)
      return item ? item.label : '-'
    },
    // 取状态着色
    getStatusColor(status) {
      const item = STATUS_OPTIONS.find((o) => o.value === status)
      return item ? item.color : 'default'
    },
    // 取下单模式中文标签
    getOrderModeLabel(orderMode) {
      return ORDER_MODE_LABEL[orderMode] || '-'
    },
    // 终态判定：8=已取消 / 9=已完成 不再允许强制关闭
    isClosedStatus(status) {
      return status === 8 || status === 9
    },
    // 商品分组的 service_item_names 拼接（后端可能返回 string[] 或 string）
    formatServiceItemNames(record) {
      const serviceItems = record && record.serviceItems
      if (Array.isArray(serviceItems) && serviceItems.length > 0) {
        const names = serviceItems
          .map(item => item.serviceItemNameSnapshot || item.name)
          .filter(Boolean)
        return names.length ? names.join('、') : '-'
      }
      const names = record && record.serviceItemNames
      if (Array.isArray(names)) {
        return names.length > 0 ? names.join('、') : '-'
      }
      return typeof names === 'string' && names.trim() ? names : '-'
    },
    normalizeItemGroups(itemGroups) {
      if (!Array.isArray(itemGroups)) {
        return []
      }
      return itemGroups.map((item, index) => {
        const group = item.group || item || {}
        const images = Array.isArray(item.images)
          ? item.images.map(img => img.imageUrl || img.url || img).filter(Boolean)
          : []
        const serviceItems = Array.isArray(item.serviceItems) ? item.serviceItems : []
        return {
          id: group.id || index,
          productName: group.productNameSnapshot || group.productName || '-',
          quantity: group.quantity,
          unitPrice: group.unitPrice,
          remark: group.remark || '',
          serviceItems,
          serviceItemNames: group.serviceItemNames,
          images
        }
      })
    },
    handlePreviewImage(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    // 校验时间区间合法性
    // 仅在两端都填写时校验:起 ≤ 终,且跨度 ≤ 366 天
    // 返回 { ok, startTime, endTime, message }
    validateTimeRange() {
      const range = this.query.timeRange || []
      const start = range[0]
      const end = range[1]

      // 未填或填一半时,后端可接受单端缺省;前端不再前置拦截
      if (!start || !end) {
        return { ok: true, startTime: start || undefined, endTime: end || undefined }
      }

      const startMoment = moment(start)
      const endMoment = moment(end)
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return { ok: false, message: '时间格式不合法' }
      }
      if (endMoment.isBefore(startMoment)) {
        return { ok: false, message: '结束时间不能早于起始时间' }
      }
      // 跨度校验:严格按天计算,与后端 366 天上限保持一致
      const diffDays = endMoment.diff(startMoment, 'days', true)
      if (diffDays > 366) {
        return { ok: false, message: '时间跨度不能超过 366 天' }
      }

      return {
        ok: true,
        startTime: startMoment.toISOString(),
        endTime: endMoment.toISOString()
      }
    },
    // 加载列表
    loadData() {
      // 先做时间区间校验,避免无效请求白跑一次
      const checked = this.validateTimeRange()
      if (!checked.ok) {
        this.$message.error(checked.message)
        return
      }

      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        merchantId: this.query.merchantId || undefined,
        auditStatus: this.query.auditStatus,
        startTime: checked.startTime,
        endTime: checked.endTime
      }
      if (this.query.status === 'CANCEL_REQUEST') {
        params.cancelRequested = true
      } else if (this.query.status === 'ONLINE') {
        params.statusList = ONLINE_STATUS_LIST.join(',')
      } else {
        params.status = this.query.status
      }
      getAppTaskList(params)
        .then(res => {
          // 兼容 MyBatis-Plus Page: { records, total }
          // 同时兼容 { list, total } 与数组返回
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 查询
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    // 重置
    handleReset() {
      this.query = {
        merchantId: undefined,
        status: 'ONLINE',
        auditStatus: undefined,
        timeRange: []
      }
      this.pagination.current = 1
      this.loadData()
    },
    // 表格分页
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    // 设置或取消顶置；顶置成功后后端会刷新 topAt，新顶置任务自动排到最前。
    handleSetTop(record) {
      if (!record || this.topSubmittingId !== null) {
        return
      }
      const top = record.isTop !== 1
      this.topSubmittingId = record.id
      setAppTaskTop(record.id, { top })
        .then(() => {
          this.$message.success(top ? '任务已顶置' : '已取消顶置')
          this.pagination.current = 1
          this.loadData()
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '顶置操作失败')
        })
        .finally(() => {
          this.topSubmittingId = null
        })
    },
    handleRejectCancellation(record) {
      this.$confirm({
        title: '拒绝该取消申请？',
        content: '请确认已联系商家并核实申请原因。拒绝后订单将继续履行。',
        okText: '拒绝取消',
        cancelText: '返回',
        onOk: () => rejectAppTaskCancellation(record.id, { reason: '经客服核实，订单需继续履行' })
          .then(() => { this.$message.success('已拒绝取消申请'); this.loadData() })
      })
    },
    handleForceComplete(record) {
      if (!record || !record.canForceComplete) return
      this.$confirm({
        title: '确认完成该订单？',
        content: '师傅提交验收已满72小时。请核实服务完成情况，确认后订单将结算给师傅，无法撤回。',
        okText: '确认完成',
        cancelText: '取消',
        onOk: () => forceCompleteAppTask(record.id, { reason: '客服核实后完成订单' })
          .then(() => {
            this.$message.success('订单已完成')
            this.loadData()
            if (this.detailVisible && this.detail.task && this.detail.task.id === record.id) {
              this.handleOpenDetail(record)
            }
          })
          .catch(err => {
            this.$message.error(err && err.message ? err.message : '订单完成失败')
          })
      })
    },
    // 打开详情弹窗
    handleOpenDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = { task: null, biddings: [], itemGroups: [], requiredCerts: [], statusLog: [] }
      getAppTaskDetail(record.id)
        .then(res => {
          // 后端约定返回 { task, biddings, itemGroups, statusLog }
          // 旧接口可能仅返回 { task, biddings }，兼容默认空数组
          const payload = (res && res.data) || {}
          this.detail = {
            task: payload.task || null,
            biddings: Array.isArray(payload.biddings) ? payload.biddings : [],
            itemGroups: this.normalizeItemGroups(payload.itemGroups),
            requiredCerts: Array.isArray(payload.requiredCerts) ? payload.requiredCerts : [],
            statusLog: Array.isArray(payload.statusLog) ? payload.statusLog : []
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载详情失败')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    // 关闭详情弹窗
    handleCloseDetail() {
      this.detailVisible = false
      this.detail = { task: null, biddings: [], itemGroups: [], requiredCerts: [], statusLog: [] }
    },
    // 打开强制关闭弹窗
    handleOpenClose(record) {
      // 终态（8 已取消 / 9 已完成）任务直接拦截,避免无效请求
      if (this.isClosedStatus(record.status)) {
        this.$message.warning('该任务已处于终态,无需关闭')
        return
      }
      this.closeForm = {
        id: record.id,
        reason: record.cancelRequestReason || ''
      }
      this.closeVisible = true
    },
    // 关闭强制关闭弹窗
    handleCloseCloseModal() {
      this.closeVisible = false
      this.closeForm = { id: null, reason: '' }
    },
    // 提交强制关闭
    handleSubmitClose() {
      const reason = (this.closeForm.reason || '').trim()
      if (!reason) {
        this.$message.error('请输入关闭原因')
        return
      }
      if (reason.length > 200) {
        this.$message.error('关闭原因不能超过 200 字')
        return
      }
      this.closeSubmitting = true
      forceCloseAppTask(this.closeForm.id, { reason })
        .then(() => {
          this.$message.success('订单已取消')
          this.closeVisible = false
          this.closeForm = { id: null, reason: '' }
          this.loadData()
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '强制关闭失败')
        })
        .finally(() => {
          this.closeSubmitting = false
        })
    }
  }
}
</script>
